import { supabase } from '@/lib/supabase/client';
import { createNotFoundError, normalizeError } from '@/lib/services/errors';
import { humanizeAddressText } from '@/lib/utils/propertyFormatting';
import type { Database, Json } from '@/lib/supabase/types.gen';
import type { PostgrestFilterBuilder } from '@supabase/postgrest-js';
import type {
  PublicPropertyDetail,
  PublicPropertyFilterState,
  PublicPropertyFormValues,
  PublicPropertyListResult,
  PublicPropertySummary,
  PublishedUnitSummary,
} from '@/lib/types/publicProperty';
import type {
  FloorLevelCode,
  FloorPlanCanvasConfig,
  RentBasisType,
  RentFrequencyType,
  UnitAvailabilityStatus,
} from '@/lib/types/property';

const DEFAULT_PAGE_SIZE = 12;
const MAX_PAGE_SIZE = 60;

type PublishableRow = Database['public']['Views']['publishable_properties_view']['Row'];
type PropertyRow = Database['public']['Tables']['properties']['Row'];

function createPublishableViewQuery() {
  return supabase.from('publishable_properties_view');
}

function _createPropertiesQuery() {
  return supabase.from('properties');
}

type PublishableViewFilterBuilder = PostgrestFilterBuilder<
  Database['__InternalSupabase'],
  Database['public'],
  PublishableRow,
  PublishableRow
>;

export interface ListPublicPropertiesParams {
  page?: number;
  pageSize?: number;
  filters?: PublicPropertyFilterState;
  vacancyOnly?: boolean;
  signal?: AbortSignal;
}

export interface GetPublicPropertyParams {
  identifier: string;
  signal?: AbortSignal;
}

export async function listPublicProperties({
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE,
  filters,
  vacancyOnly = false,
  signal: _signal,
}: ListPublicPropertiesParams = {}): Promise<PublicPropertyListResult> {
  const safePage = normalizePositiveInteger(page, 1);
  const safePageSize = clampInteger(pageSize, 1, MAX_PAGE_SIZE, DEFAULT_PAGE_SIZE);
  const from = (safePage - 1) * safePageSize;
  const to = from + safePageSize - 1;

  const baseQueryUntyped = createPublishableViewQuery()
    .select('*', { count: 'exact' })
    .order('earliest_availability', { ascending: true, nullsFirst: true })
    .order('property_code', { ascending: true });
  const baseQuery = baseQueryUntyped as unknown as PublishableViewFilterBuilder;

  const filteredRelation: PublishableViewFilterBuilder = filters
    ? applyPublishableFilters(baseQuery, filters)
    : baseQuery;

  const vacancyRelation = vacancyOnly ? filteredRelation.eq('has_vacancy', true) : filteredRelation;
  const query = vacancyRelation.range(from, to);
  const { data, count } = await query.throwOnError();

  const rows = Array.isArray(data) ? data.filter(isPublishableRow) : [];
  const items = rows.map(mapPublishableRowToSummary);
  const totalItems = typeof count === 'number' ? count : items.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / safePageSize));

  return {
    items,
    totalItems,
    totalPages,
    page: safePage,
    pageSize: safePageSize,
  };
}

export async function getPublicProperty({
  identifier,
  signal: _signal,
}: GetPublicPropertyParams): Promise<PublicPropertyDetail> {
  const normalized = identifier.trim();

  if (normalized.length === 0) {
    throw createNotFoundError('Property not found.');
  }

  const column = isUuid(normalized) ? 'id' : 'property_code';
  const value = column === 'property_code' ? normalized.toUpperCase() : normalized;
  const viewQuery = createPublishableViewQuery().select('*').eq(column, value).maybeSingle();
  const { data } = await viewQuery.throwOnError();

  if (!isPublishableRow(data)) {
    throw createNotFoundError('Property not found.');
  }

  const summary = mapPublishableRowToSummary(data);
  const property = mapPublishableRowToFormValues(data);

  try {
    return { summary, property } satisfies PublicPropertyDetail;
  } catch (caught) {
    throw normalizeError(caught);
  }
}

function applyPublishableFilters(
  builder: PublishableViewFilterBuilder,
  filters: PublicPropertyFilterState,
): PublishableViewFilterBuilder {
  let next = builder;

  if (filters.submarkets.length > 0) {
    next = next.in('submarket', filters.submarkets);
  }

  if (filters.assetTypes.length > 0) {
    next = next.in('asset_type', filters.assetTypes);
  }

  const rentRange = filters.rentPerSquareFoot;
  if (rentRange.min != null) {
    next = next.gte('max_net_rent', rentRange.min);
  }
  if (rentRange.max != null) {
    next = next.lte('min_net_rent', rentRange.max);
  }

  const sfRange = filters.rentableSquareFeet;
  if (sfRange.min != null) {
    next = next.gte('max_unit_rentable_sf', sfRange.min);
  }
  if (sfRange.max != null) {
    next = next.lte('min_unit_rentable_sf', sfRange.max);
  }

  if (filters.incentivesOnly) {
    next = next.eq('has_incentives', true);
  }

  if (filters.readinessStates.length > 0) {
    next = next.overlaps('readiness_states', filters.readinessStates);
  }

  if (filters.availability.start !== null) {
    next = next.gte('latest_availability', filters.availability.start);
  }
  if (filters.availability.end !== null) {
    next = next.lte('earliest_availability', filters.availability.end);
  }

  if (typeof filters.potentiallyDivisible === 'boolean') {
    next = next.eq('has_divisible', filters.potentiallyDivisible);
  }

  return next;
}

function mapPublishableRowToSummary(row: PublishableRow): PublicPropertySummary {
  const publishedUnits = parsePublishedUnits(row.published_units);
  const hasVacancy =
    typeof row.has_vacancy === 'boolean' ? row.has_vacancy : publishedUnits.length > 0;
  const readinessStates = parseReadinessStates(row.readiness_states, publishedUnits);
  const coverImageUrl = resolveCoverImage(row.hero_media_urls);
  const coverImageAlt =
    typeof coverImageUrl === 'string' && coverImageUrl.length > 0 ? formatAddress(row) : null;

  return {
    id: row.id ?? '',
    propertyCode: (row.property_code ?? '').toUpperCase(),
    address: {
      streetNumber: row.street_number ?? '',
      streetName: row.street_name ?? '',
      streetType: row.street_type ?? 'St',
      streetDirection: row.street_direction,
      suiteNumber: row.suite_number,
      city: row.city ?? '',
      province: row.province ?? 'ON',
      postalCode: row.postal_code ?? '',
    },
    submarket: row.submarket ?? '',
    assetType: row.asset_type ?? 'industrial',
    totalRentableSf: row.total_rentable_sf ?? 0,
    marketingSummary: row.marketing_summary ?? 'Details coming soon.',
    heroMediaUrls: row.hero_media_urls ?? [],
    coverImageUrl,
    coverImageAlt,
    floorPlanMode: row.floor_plan_mode ?? 'hotspot',
    floorPlanImageUrl: row.floor_plan_image_url,
    floorPlanConfig: parseFloorPlanConfig(row.floor_plan_config),
    publishedUnits,
    publishedUnitCount: publishedUnits.length,
    minUnitRentableSf: row.min_unit_rentable_sf,
    maxUnitRentableSf: row.max_unit_rentable_sf,
    minNetRent: row.min_net_rent,
    maxNetRent: row.max_net_rent,
    earliestAvailability: row.earliest_availability,
    latestAvailability: row.latest_availability,
    readinessStates,
    hasVacancy,
    hasIncentives: Boolean(row.has_incentives),
    hasPotentiallyDivisible: Boolean(row.has_divisible),
    mediaGallery: [],
  } satisfies PublicPropertySummary;
}

function parseReadinessStates(
  value: PublishableRow['readiness_states'],
  fallbackUnits: PublishedUnitSummary[],
): UnitAvailabilityStatus[] {
  if (Array.isArray(value)) {
    return value.filter(
      (entry): entry is UnitAvailabilityStatus =>
        entry === 'available' || entry === 'unavailable' || entry === 'leased',
    );
  }

  return Array.from(new Set(fallbackUnits.map((unit) => unit.availabilityStatus)));
}

function _mapPropertyRowToFormValues(row: PropertyRow): PublicPropertyFormValues {
  return {
    id: row.id,
    propertyCode: row.property_code,
    submarket: row.submarket,
    assetType: row.asset_type,
    totalRentableSf: row.total_rentable_sf,
    marketingSummary: row.marketing_summary,
    heroMediaUrls: row.hero_media_urls,
    floorPlanMode: row.floor_plan_mode,
    floorPlanImageUrl: row.floor_plan_image_url,
    floorPlanConfig: parseFloorPlanConfig(row.floor_plan_config),
    address: {
      streetNumber: row.street_number,
      streetName: row.street_name,
      streetType: row.street_type,
      streetDirection: row.street_direction,
      suiteNumber: row.suite_number,
      city: row.city,
      province: row.province,
      postalCode: row.postal_code,
    },
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    createdBy: row.created_by,
    updatedBy: row.updated_by,
  };
}

function mapPublishableRowToFormValues(row: PublishableRow): PublicPropertyFormValues {
  return {
    id: row.id ?? undefined,
    propertyCode: row.property_code ?? '',
    submarket: row.submarket ?? '',
    assetType: row.asset_type ?? 'industrial',
    totalRentableSf: row.total_rentable_sf ?? 0,
    marketingSummary: row.marketing_summary ?? 'Details coming soon.',
    heroMediaUrls: row.hero_media_urls ?? [],
    floorPlanMode: row.floor_plan_mode ?? 'hotspot',
    floorPlanImageUrl: row.floor_plan_image_url ?? null,
    floorPlanConfig: parseFloorPlanConfig(row.floor_plan_config),
    address: {
      streetNumber: row.street_number ?? '',
      streetName: row.street_name ?? '',
      streetType: row.street_type ?? 'St',
      streetDirection: row.street_direction,
      suiteNumber: row.suite_number,
      city: row.city ?? '',
      province: row.province ?? 'ON',
      postalCode: row.postal_code ?? '',
    },
    createdAt: undefined,
    updatedAt: undefined,
    createdBy: null,
    updatedBy: null,
  } satisfies PublicPropertyFormValues;
}

function parsePublishedUnits(value: Json | null): PublishedUnitSummary[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => {
      if (typeof entry !== 'object' || entry === null) {
        return null;
      }
      const record = entry as Record<string, unknown>;
      const id = typeof record.id === 'string' ? record.id : null;
      const unitCode = typeof record.unitCode === 'string' ? record.unitCode : null;
      const unitLabelRaw =
        typeof record.unitLabel === 'string' && record.unitLabel.trim().length > 0
          ? record.unitLabel.trim()
          : null;
      const unitLabel = unitLabelRaw ?? unitCode;
      const rawFloorLevel =
        typeof record.floorLevel === 'string'
          ? record.floorLevel
          : typeof record.floorLabel === 'string'
            ? record.floorLabel
            : null;
      const floorLevel =
        typeof rawFloorLevel === 'string' && rawFloorLevel.trim().length > 0
          ? (rawFloorLevel.trim() as FloorLevelCode)
          : null;
      const areaSF = toNumber(record.areaSF, null);
      const minDivisibleSF = toNumber(record.minDivisibleSF, null);
      const availabilityStart =
        typeof record.availabilityStart === 'string' ? record.availabilityStart : null;
      const availabilityEnd =
        typeof record.availabilityEnd === 'string' ? record.availabilityEnd : null;
      const availabilityStatus =
        typeof record.availabilityStatus === 'string'
          ? (record.availabilityStatus as UnitAvailabilityStatus)
          : null;
      const rentBasis =
        typeof record.rentBasis === 'string' ? (record.rentBasis as RentBasisType) : null;
      const sourceRentFrequency =
        typeof record.rentFrequency === 'string'
          ? (record.rentFrequency as RentFrequencyType)
          : null;
      const netRentPerSF = toNumber(record.netRentPerSF, null);
      const additionalRentPerSF = toNumber(record.additionalRentPerSF, null);
      const rentFrequency: RentFrequencyType = sourceRentFrequency ?? 'monthly';
      const incentives = typeof record.incentives === 'string' ? record.incentives : null;
      const contactEmail = typeof record.contactEmail === 'string' ? record.contactEmail : null;
      const heroImageUrl =
        typeof record.heroImageUrl === 'string' && record.heroImageUrl.trim().length > 0
          ? record.heroImageUrl.trim()
          : null;
      const galleryImageUrls = Array.isArray(record.galleryImageUrls)
        ? record.galleryImageUrls
            .filter((url): url is string => typeof url === 'string')
            .map((url) => url.trim())
            .filter((url) => url.length > 0)
        : [];
      const planGeometry = (record.planGeometry as Json) ?? null;
      const possessionTimeline =
        record.possessionTimeline === 'negotiable' || record.possessionTimeline === 'immediate'
          ? record.possessionTimeline
          : 'immediate';
      const statusUpdatedAt =
        typeof record.statusUpdatedAt === 'string'
          ? record.statusUpdatedAt
          : new Date().toISOString();

      if (
        id === null ||
        unitCode === null ||
        unitLabel === null ||
        floorLevel === null ||
        availabilityStatus === null ||
        availabilityStart === null ||
        netRentPerSF === null ||
        rentBasis === null
      ) {
        return null;
      }

      return {
        id,
        unitCode,
        unitLabel,
        floorLevel,
        areaSF: areaSF ?? 0,
        minDivisibleSF: minDivisibleSF ?? areaSF ?? 0,
        availabilityStart,
        availabilityEnd,
        availabilityStatus,
        rentBasis,
        netRentPerSF,
        additionalRentPerSF,
        rentFrequency,
        incentives,
        potentiallyDivisible: Boolean(record.potentiallyDivisible),
        contactEmail: contactEmail ?? '',
        heroImageUrl,
        galleryImageUrls,
        planGeometry,
        statusUpdatedAt,
        possessionTimeline,
      } satisfies PublishedUnitSummary;
    })
    .filter((unit): unit is PublishedUnitSummary => Boolean(unit));
}

function parseFloorPlanConfig(value: Json | null): FloorPlanCanvasConfig {
  if (value !== null && typeof value === 'object') {
    return value as FloorPlanCanvasConfig;
  }
  return {};
}

function resolveCoverImage(heroMediaUrls: string[] | null): string | null {
  const urls = Array.isArray(heroMediaUrls) ? heroMediaUrls : null;
  if (urls === null || urls.length === 0) {
    return null;
  }
  const primary = urls.find((url) => typeof url === 'string' && url.trim().length > 0);
  return typeof primary === 'string' ? primary.trim() : null;
}

function formatAddress(row: PublishableRow): string {
  return [
    humanizeAddressText(row.street_number),
    humanizeAddressText(row.street_name),
    humanizeAddressText(row.street_type),
    humanizeAddressText(row.street_direction),
  ]
    .filter(Boolean)
    .join(' ');
}

function normalizePositiveInteger(value: number, fallback: number): number {
  if (!Number.isFinite(value) || value <= 0) {
    return fallback;
  }
  return Math.floor(value);
}

function clampInteger(value: number, min: number, max: number, fallback: number): number {
  if (!Number.isFinite(value)) {
    return fallback;
  }
  return Math.min(Math.max(Math.floor(value), min), max);
}

function toNumber(value: unknown, fallback: number | null): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value);
    return Number.isNaN(parsed) ? fallback : parsed;
  }
  return fallback;
}

function isUuid(value: string): boolean {
  return /^[0-9a-fA-F-]{36}$/.test(value);
}

function isPublishableRow(value: unknown): value is PublishableRow {
  return value !== null && typeof value === 'object';
}

function _isPropertyRow(value: unknown): value is PropertyRow {
  return value !== null && typeof value === 'object';
}
